import type {
  CommerceHelpCalloutViewModel,
  PublicationProductSelectionValue,
  PublicationSelectorRuleViewModel,
} from '@/features/commerce-admin/headless-contract';
import type { Product, ProductClassification, ProductVariant } from '@/lib/commerce';

type WarningContext = {
  label: string;
  selections: PublicationProductSelectionValue[];
};

type BundleConflict = {
  parentLabel: string;
  componentLabel: string;
  reason: 'excluded' | 'not_ready' | 'missing';
  contexts: Set<string>;
};

type BuildPublicationBundleWarningsInput = {
  products: Product[];
  selection: PublicationProductSelectionValue;
};

function normalized(value: string | null | undefined): string {
  return (value || '').trim().toLowerCase();
}

function isStorefrontReadyProduct(product: Product): boolean {
  return normalized(product.status) === 'active' && normalized(product.publish_status) === 'published';
}

function isStorefrontReadyVariant(variant: ProductVariant): boolean {
  return normalized(variant.status) === 'active' && variant.sellable;
}

function matchText(operator: string, candidate: string | null | undefined, values: string[]): boolean {
  const normalizedCandidate = normalized(candidate);
  if (operator === 'exists') return normalizedCandidate.length > 0;
  if (operator === 'contains') return values.some(value => normalizedCandidate.includes(normalized(value)));
  return values.some(value => normalizedCandidate === normalized(value));
}

function matchClassification(
  rule: PublicationSelectorRuleViewModel,
  classifications: ProductClassification[] | undefined,
): boolean {
  return (classifications || []).some(classification => {
    if (rule.selectorKey && normalized(classification.classification_key) !== normalized(rule.selectorKey)) {
      return false;
    }
    return matchText(rule.operator, classification.classification_value, rule.valueLabels);
  });
}

function matchesRule(
  product: Product,
  candidateVariantIds: Set<string>,
  rule: PublicationSelectorRuleViewModel,
): boolean {
  switch (rule.attribute) {
    case 'product_id':
      return matchText(rule.operator, product.product_id, rule.valueLabels);
    case 'variant_id':
      return product.variants.some(variant =>
        candidateVariantIds.has(variant.variant_id)
        && matchText(rule.operator, variant.variant_id, rule.valueLabels));
    case 'product_type':
      return matchText(rule.operator, product.product_type_key, rule.valueLabels);
    case 'service_class':
      return matchText(rule.operator, product.service_class, rule.valueLabels);
    case 'service_group':
      return matchText(rule.operator, product.service_group, rule.valueLabels);
    case 'facet':
      return matchClassification(rule, product.facets);
    case 'flag':
      return matchClassification(rule, product.flags);
    default:
      return false;
  }
}

function applySelection(
  products: Product[],
  candidateVariantIds: Set<string>,
  selection: PublicationProductSelectionValue,
): Set<string> {
  if (selection.mode === 'all_products') return new Set(candidateVariantIds);
  if (selection.mode === 'explicit_variants') {
    const included = new Set(selection.includedVariantIds);
    return new Set([...candidateVariantIds].filter(variantId => included.has(variantId)));
  }

  let includedProductIds: Set<string>;
  if (selection.mode === 'explicit_products') {
    includedProductIds = new Set(selection.includedProductIds);
  } else {
    includedProductIds = new Set(products
      .filter(product => product.variants.some(variant => candidateVariantIds.has(variant.variant_id)))
      .filter(product => selection.rules.every(rule => matchesRule(product, candidateVariantIds, rule)))
      .map(product => product.product_id));
  }

  return new Set(products
    .filter(product => includedProductIds.has(product.product_id))
    .flatMap(product => product.variants)
    .map(variant => variant.variant_id)
    .filter(variantId => candidateVariantIds.has(variantId)));
}

function variantLabel(product: Product, variant: ProductVariant): string {
  return `${product.name} / ${variant.display_name || variant.name}`;
}

function formatContexts(contexts: Set<string>): string {
  const labels = [...contexts];
  if (labels.length <= 3) return labels.join(', ');
  return `${labels.slice(0, 3).join(', ')} and ${labels.length - 3} more markets`;
}

export function buildPublicationBundleWarnings({
  products,
  selection,
}: BuildPublicationBundleWarningsInput): CommerceHelpCalloutViewModel[] {
  const productsById = new Map(products.map(product => [product.product_id, product]));
  const variantsById = new Map<string, { product: Product; variant: ProductVariant }>();
  const storefrontReadyVariantIds = new Set<string>();
  for (const product of products) {
    for (const variant of product.variants || []) {
      variantsById.set(variant.variant_id, { product, variant });
      if (isStorefrontReadyProduct(product) && isStorefrontReadyVariant(variant)) {
        storefrontReadyVariantIds.add(variant.variant_id);
      }
    }
  }

  const defaultVariantIds = applySelection(products, storefrontReadyVariantIds, selection);
  const contexts: WarningContext[] = [
    { label: 'Default selection', selections: [] },
  ];
  const conflicts = new Map<string, BundleConflict>();

  for (const context of contexts) {
    const selectedVariantIds = context.selections.reduce(
      (candidateIds, scopedSelection) => applySelection(products, candidateIds, scopedSelection),
      new Set(defaultVariantIds),
    );

    for (const parentVariantId of selectedVariantIds) {
      const parent = variantsById.get(parentVariantId);
      if (!parent) continue;
      for (const component of parent.variant.bundle_components || []) {
        if (!component.required) continue;

        let componentLabel = '';
        let reason: BundleConflict['reason'] | null = null;
        let conflictKey = `${parentVariantId}:${component.bundle_component_id}`;
        if (component.component_variant_id) {
          const target = variantsById.get(component.component_variant_id);
          componentLabel = target
            ? variantLabel(target.product, target.variant)
            : `variant ${component.component_variant_id}`;
          if (!target) reason = 'missing';
          else if (!storefrontReadyVariantIds.has(component.component_variant_id)) reason = 'not_ready';
          else if (!selectedVariantIds.has(component.component_variant_id)) reason = 'excluded';
        } else if (component.component_product_id) {
          const targetProduct = productsById.get(component.component_product_id);
          componentLabel = targetProduct?.name || `product ${component.component_product_id}`;
          conflictKey = `${parentVariantId}:${component.bundle_component_id}:${component.component_product_id}`;
          if (!targetProduct) reason = 'missing';
          else {
            const readyTargetIds = targetProduct.variants
              .map(variant => variant.variant_id)
              .filter(variantId => storefrontReadyVariantIds.has(variantId));
            if (readyTargetIds.length === 0) reason = 'not_ready';
            else if (!readyTargetIds.some(variantId => selectedVariantIds.has(variantId))) reason = 'excluded';
          }
        }
        if (!reason) continue;

        const conflict = conflicts.get(conflictKey) || {
          parentLabel: variantLabel(parent.product, parent.variant),
          componentLabel,
          reason,
          contexts: new Set<string>(),
        };
        conflict.contexts.add(context.label);
        conflicts.set(conflictKey, conflict);
      }
    }
  }

  if (conflicts.size === 0) return [];
  const reasonLabels: Record<BundleConflict['reason'], string> = {
    excluded: 'is excluded by the resolved product selection',
    not_ready: 'is not active, published, and sellable',
    missing: 'no longer exists in this catalog',
  };
  const visibleConflicts = [...conflicts.values()].slice(0, 4);
  const details = visibleConflicts.map(conflict =>
    `${formatContexts(conflict.contexts)}: ${conflict.parentLabel} requires ${conflict.componentLabel}, which ${reasonLabels[conflict.reason]}.`);
  if (conflicts.size > visibleConflicts.length) {
    details.push(`${conflicts.size - visibleConflicts.length} additional bundle dependency conflicts are also present.`);
  }

  return [{
    id: 'publication-bundle-component-conflicts',
    title: `${conflicts.size} required bundle ${conflicts.size === 1 ? 'component is' : 'components are'} unavailable`,
    body: `${details.join(' ')} Availability and checkout will fail for the affected parent variants until the required components are included and storefront-ready.`,
    tone: 'warning',
  }];
}
